import React, { useState } from 'react';
import { Flex, Gutter, Title, BodyText, Dropdown, GridBag, Autocomplete, Icon } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { AdminCategory, Tag } from '@cdaas/api-clients/dist/product-service-openapi';

import { Errors } from '../utils';

import './Discoverability.scss';

interface DiscoverabilityProps {
  title: string;
  productGategoryIds: number[] | undefined;
  productTagIds: number[] | undefined;
  allCategoriesOfSystem: AdminCategory[];
  allTagsOfSystem: Tag[];
  handleSubCategoriesChange: (subCategories: number[]) => void;
  handleTagsChange: (tags: number[]) => void;
  categoryError: Errors['category'];
}

function Discoverability(props: DiscoverabilityProps) {
  const {
    title,
    productGategoryIds,
    productTagIds,
    allCategoriesOfSystem,
    allTagsOfSystem,
    handleSubCategoriesChange,
    handleTagsChange,
    categoryError,
  } = props;
  const { t } = useTranslation('productscreen');

  const [currentTagText, setCurrentTagText] = useState<string>('');

  const filterTags = (tagText: string) => {
    return allTagsOfSystem.filter(
      tag => !(productTagIds || []).includes(tag.id) && tag.name.toLocaleLowerCase().includes(tagText.toLocaleLowerCase()),
    );
  };
  const [filteredTags, setFilteredTags] = useState<Tag[]>(filterTags(currentTagText));

  const mainCategory = allCategoriesOfSystem.find(category => {
    return category.subcategories?.find(subCategory => subCategory.id === productGategoryIds?.[0]);
  });

  const subCategories = allCategoriesOfSystem.find(category => category.id === mainCategory?.id)?.subcategories || [];
  const subCategory = subCategories.find(subCategory => subCategory.id === productGategoryIds?.[0]);

  const handleSubCategoryChange = (subCategoryId: number) => {
    handleSubCategoriesChange([subCategoryId]);
  };

  const handleMainCategoryChange = (mainCategoryId: number) => {
    const subCategories = allCategoriesOfSystem.find(category => category.id === mainCategoryId)?.subcategories || [];
    if (subCategories.length > 0) {
      handleSubCategoriesChange([subCategories[0].id]);
    }
  };

  const handleCurrentTagTextChange = (tagText: string) => {
    setCurrentTagText(tagText);
    setFilteredTags(filterTags(tagText));
  };

  const handleTagAdd = (tagId: string) => {
    handleTagsChange([...(productTagIds || []), Number(tagId)]);
    setCurrentTagText('');
  };

  const handleTagRemove = (tagId: number) => {
    handleTagsChange((productTagIds || []).filter(id => id !== tagId));
  };

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('discoverability_section.info')}</BodyText>
      <Gutter />
      <GridBag>
        <GridBag.Item md={4}>
          <Dropdown
            name="category"
            label={t('discoverability_section.category_label')}
            onChange={(_, option) => handleMainCategoryChange(Number(option.value))}
            selectedOption={mainCategory && { label: mainCategory.name, value: mainCategory.id.toString() }}
            options={allCategoriesOfSystem.map(category => {
              return { label: category.name, value: category.id.toString() };
            })}
            placeholder={t('discoverability_section.category_placeholder')}
            required={true}
            errorText={categoryError}
          />
        </GridBag.Item>
        <GridBag.Item md={3}>
          <Dropdown
            name="subCategory"
            label={t('discoverability_section.sub_category_label')}
            onChange={(_, option) => handleSubCategoryChange(Number(option.value))}
            selectedOption={subCategory && { label: subCategory.name, value: subCategory.id.toString() }}
            options={subCategories.map(subCategory => ({ label: subCategory.name, value: subCategory.id.toString() }))}
            placeholder={t('discoverability_section.sub_category_placeholder')}
            required={true}
          />
        </GridBag.Item>
      </GridBag>
      <Gutter size="sm" />
      <GridBag>
        <GridBag.Item md={7}>
          <Flex directionColumn>
            <Autocomplete
              name={'tag'}
              label={t('discoverability_section.tags_label')}
              onChange={(_, tagText) => handleCurrentTagTextChange(tagText)}
              onOptionSelect={(_, tagOption) => handleTagAdd(tagOption.value)}
              value={currentTagText}
              options={filteredTags.map(tag => ({ label: tag.name, value: tag.id.toString() }))}
              placeholder={t('discoverability_section.tag_placeholder')}
              type="text"
            />
            <Gutter />
            <Flex wrapContent={true}>
              {(productTagIds || []).map(tagId => {
                const tag = allTagsOfSystem.find(tag => tag.id === tagId);
                return (
                  <Flex
                    centeredVertically
                    occupyVerticalSpace={false}
                    key={tagId}
                    margin={{ right: 'md', bottom: 'sm' }}
                    padding={{ size: 'xs' }}
                    className="product-tag-listing__product-tag"
                  >
                    <BodyText size={'md'} fontWeight={'bold'} tagName={'p'} block={false} color={'black'}>
                      {tag?.name}
                    </BodyText>
                    <Icon
                      data-testid={`removeTag-${tag?.id}-${tag?.name}`}
                      title={t('discoverability_section.remove_tag')}
                      color="gray500"
                      name="delete"
                      size="sm"
                      margin={{ left: 'xs' }}
                      clickableAppearance
                      onClick={() => handleTagRemove(tagId)}
                    />
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </GridBag.Item>
      </GridBag>
    </Flex>
  );
}

export default React.memo(Discoverability);
